import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  History,
  Download,
  X,
  Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelPolicyMaster = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [policies, setPolicies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editPolicy, setEditPolicy] = useState(null);
  const [historyPolicy, setHistoryPolicy] = useState(null);
  const [deletePolicy, setDeletePolicy] = useState(null);

  // Form State
  const initialForm = {
    policy_name: '',
    company_id: 'all',
    branch: 'all',
    department: 'all',
    designation: 'all',
    grade: 'all',
    employment_type: 'all',
    is_default: false,
    allowed_transport_modes: ['flight', 'train', 'bus', 'cab'],
    allowed_flight_class: ['economy'],
    allowed_train_class: ['3ac', '2ac'],
    allowed_bus_class: ['ac_sleeper'],
    allowed_cab_type: ['sedan'],
    hotel_star_rating_limit: 4,
    max_daily_hotel_limit: 5000,
    max_daily_meal_allowance: 1500,
    max_local_travel_limit: 2000,
    max_trip_budget: 50000,
    advance_booking_days: 7,
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '2030-12-31',
    approval_required: true,
    cancellation_rules: 'Standard cancellation terms apply.',
    weekend_travel_allowed: true,
    international_rules: 'Requires VP & CFO Approval.',
    status: 'active',
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/travel-desk/policies`, { withCredentials: true });
      setPolicies(data.policies || []);
    } catch (err) {
      toast.error('Failed to load travel policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePolicy = async (e) => {
    e.preventDefault();
    if (!formData.policy_name) {
      toast.error('Please enter policy name');
      return;
    }

    try {
      if (editPolicy) {
        await axios.put(`${API_URL}/travel-desk/policies/${editPolicy._id}`, formData, { withCredentials: true });
        toast.success('Travel Policy updated (version incremented)!');
      } else {
        await axios.post(`${API_URL}/travel-desk/policies`, formData, { withCredentials: true });
        toast.success('New Travel Policy created successfully!');
      }
      setShowCreateModal(false);
      setEditPolicy(null);
      setFormData(initialForm);
      fetchPolicies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save policy');
    }
  };

  const togglePolicyStatus = async (policyId, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await axios.patch(`${API_URL}/travel-desk/policies/${policyId}/status`, { status: nextStatus }, { withCredentials: true });
      toast.success(`Policy status set to ${nextStatus}`);
      fetchPolicies();
    } catch (err) {
      toast.error('Failed to toggle policy status');
    }
  };

  const handleDeletePolicy = async (policyId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/policies/${policyId}`, { withCredentials: true });
      toast.success('Policy deleted successfully');
      setDeletePolicy(null);
      fetchPolicies();
    } catch (err) {
      toast.error('Failed to delete policy');
    }
  };

  const openEdit = (p) => {
    setEditPolicy(p);
    setFormData({
      policy_name: p.policy_name || '',
      company_id: p.company_id || 'all',
      branch: p.branch || 'all',
      department: p.department || 'all',
      designation: p.designation || 'all',
      grade: p.grade || 'all',
      employment_type: p.employment_type || 'all',
      is_default: Boolean(p.is_default),
      allowed_transport_modes: p.allowed_transport_modes || ['flight', 'train', 'bus', 'cab'],
      allowed_flight_class: p.allowed_flight_class || ['economy'],
      allowed_train_class: p.allowed_train_class || ['3ac', '2ac'],
      allowed_bus_class: p.allowed_bus_class || ['ac_sleeper'],
      allowed_cab_type: p.allowed_cab_type || ['sedan'],
      hotel_star_rating_limit: p.hotel_star_rating_limit || 4,
      max_daily_hotel_limit: p.max_daily_hotel_limit || 5000,
      max_daily_meal_allowance: p.max_daily_meal_allowance || 1500,
      max_local_travel_limit: p.max_local_travel_limit || 2000,
      max_trip_budget: p.max_trip_budget || 50000,
      advance_booking_days: p.advance_booking_days || 7,
      effective_from: p.effective_from || new Date().toISOString().split('T')[0],
      effective_to: p.effective_to || '2030-12-31',
      approval_required: p.approval_required !== false,
      cancellation_rules: p.cancellation_rules || '',
      weekend_travel_allowed: p.weekend_travel_allowed !== false,
      international_rules: p.international_rules || '',
      status: p.status || 'active',
    });
    setShowCreateModal(true);
  };

  const exportCSV = () => {
    const headers = ['Policy Name', 'Version', 'Grade', 'Dept', 'Flight Class', 'Train Class', 'Hotel Cap', 'Meal Cap', 'Status'];
    const rows = filteredPolicies.map(p => [
      `"${p.policy_name}"`, p.version || '1.0', p.grade, p.department,
      (p.allowed_flight_class || []).join('/'), (p.allowed_train_class || []).join('/'),
      p.max_daily_hotel_limit, p.max_daily_meal_allowance, p.status
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Travel_Policies_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredPolicies = policies.filter(p => {
    const q = searchTerm.toLowerCase();
    return (
      (p.policy_name || '').toLowerCase().includes(q) ||
      (p.department || '').toLowerCase().includes(q) ||
      (p.grade || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
            <BookOpen className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black">Travel Policy Master</h1>
            <p className="text-xs text-blue-200">
              Grade-wise entitlements, class limits, daily allowances, international travel rules &amp; caps.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={exportCSV}
            className="px-3.5 py-2 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl flex items-center gap-1.5 transition-all"
          >
            <Download className="w-3.5 h-3.5" /> Export
          </button>

          {hasPermission(user, 'travel_desk.policy.edit') && (
            <button
              onClick={() => {
                setEditPolicy(null);
                setFormData(initialForm);
                setShowCreateModal(true);
              }}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-xs rounded-xl shadow-md flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" /> Create Policy
            </button>
          )}
        </div>
      </div>

      {/* Search Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex items-center justify-between gap-4">
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search policy name, grade, department..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>
        <div className="text-xs font-bold text-slate-500">
          Total Policies: {filteredPolicies.length}
        </div>
      </div>

      {/* Grid of Policies */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredPolicies.map((p) => (
          <div key={p._id} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 space-y-4 relative">
            <div className="flex justify-between items-start border-b border-slate-200 pb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-bold text-slate-900 text-base">{p.policy_name}</h3>
                  <span className="font-mono text-[10px] px-2 py-0.5 bg-blue-100 text-blue-800 font-bold rounded-md">
                    v{p.version || '1.0'}
                  </span>
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Grade: <b>{p.grade}</b> · Dept: <b>{p.department}</b>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                  p.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
                }`}>
                  {p.status}
                </span>

                <button
                  onClick={() => setHistoryPolicy(p)}
                  className="p-1.5 bg-slate-100 text-slate-600 hover:bg-slate-200 rounded-lg text-xs font-bold"
                  title="Version History"
                >
                  <History className="w-3.5 h-3.5" />
                </button>

                {hasPermission(user, 'travel_desk.policy.edit') && (
                  <button
                    onClick={() => openEdit(p)}
                    className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg text-xs font-bold"
                    title="Edit Policy"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                )}

                <button
                  onClick={() => setDeletePolicy(p)}
                  className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg text-xs font-bold"
                  title="Delete Policy"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="text-slate-400 font-medium block">Allowed Flight Class</span>
                <span className="font-bold text-slate-900 capitalize">{(p.allowed_flight_class || []).join(', ')}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="text-slate-400 font-medium block">Allowed Train Class</span>
                <span className="font-bold text-slate-900 uppercase">{(p.allowed_train_class || []).join(', ')}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="text-slate-400 font-medium block">Max Daily Hotel Limit</span>
                <span className="font-mono font-bold text-emerald-600">₹ {(p.max_daily_hotel_limit || 0).toLocaleString('en-IN')}</span>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="text-slate-400 font-medium block">Max Daily Meal Allowance</span>
                <span className="font-mono font-bold text-emerald-600">₹ {(p.max_daily_meal_allowance || 0).toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-100 p-3 rounded-xl text-xs space-y-1">
              <div className="font-bold text-indigo-900">International Travel Rules</div>
              <div className="text-slate-600">{p.international_rules || 'Requires VP & CFO Approval'}</div>
            </div>

            <div className="flex justify-between items-center pt-2 text-[11px]">
              <button
                onClick={() => togglePolicyStatus(p._id, p.status)}
                className={`font-bold hover:underline ${p.status === 'active' ? 'text-amber-600' : 'text-emerald-600'}`}
              >
                {p.status === 'active' ? 'Deactivate Policy' : 'Activate Policy'}
              </button>
              {p.is_default && (
                <span className="text-amber-700 font-bold bg-amber-100 px-2 py-0.5 rounded text-[10px]">
                  DEFAULT ENTERPRISE POLICY
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* CREATE / EDIT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-amber-500" />
                {editPolicy ? `Edit Travel Policy (${editPolicy.policy_name})` : 'Create New Travel Policy'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 font-bold">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePolicy} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Policy Title *</label>
                <input
                  type="text"
                  required
                  value={formData.policy_name}
                  onChange={(e) => setFormData({ ...formData, policy_name: e.target.value })}
                  className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  placeholder="Senior Management Travel Policy"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={formData.company_id}
                    onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Department</label>
                  <input
                    type="text"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Grade</label>
                  <input
                    type="text"
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Allowed Flight Class</label>
                  <select
                    value={formData.allowed_flight_class[0] || 'economy'}
                    onChange={(e) => setFormData({ ...formData, allowed_flight_class: [e.target.value] })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  >
                    <option value="economy">Economy Only</option>
                    <option value="premium_economy">Premium Economy</option>
                    <option value="business">Business Class</option>
                  </select>
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Allowed Train Class</label>
                  <select
                    value={formData.allowed_train_class[0] || '3ac'}
                    onChange={(e) => setFormData({ ...formData, allowed_train_class: [e.target.value] })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none"
                  >
                    <option value="3ac">3AC / Executive Chair</option>
                    <option value="2ac">2AC / 1AC</option>
                    <option value="sleeper">Sleeper / Standard</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Daily Hotel Cap (₹)</label>
                  <input
                    type="number"
                    value={formData.max_daily_hotel_limit}
                    onChange={(e) => setFormData({ ...formData, max_daily_hotel_limit: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Daily Meal Allowance (₹)</label>
                  <input
                    type="number"
                    value={formData.max_daily_meal_allowance}
                    onChange={(e) => setFormData({ ...formData, max_daily_meal_allowance: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold"
                  />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Local Cab Cap (₹)</label>
                  <input
                    type="number"
                    value={formData.max_local_travel_limit}
                    onChange={(e) => setFormData({ ...formData, max_local_travel_limit: e.target.value })}
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl outline-none font-mono font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">International Travel Rules</label>
                <textarea
                  rows="2"
                  value={formData.international_rules}
                  onChange={(e) => setFormData({ ...formData, international_rules: e.target.value })}
                  className="w-full p-2.5 bg-slate-50 border border-slate-300 rounded-xl outline-none text-xs"
                />
              </div>

              <div className="pt-3 border-t border-slate-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-md"
                >
                  {editPolicy ? 'Update & Increment Version' : 'Save Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* VERSION HISTORY DRAWER */}
      {historyPolicy && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-200 pb-3">
              <h3 className="font-bold text-slate-900 text-sm">
                Version History — {historyPolicy.policy_name}
              </h3>
              <button onClick={() => setHistoryPolicy(null)} className="text-slate-400 font-bold">&times;</button>
            </div>

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {(historyPolicy.version_history || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No prior version edits logged.</div>
              ) : (
                historyPolicy.version_history.map((h, i) => (
                  <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs space-y-1">
                    <div className="flex justify-between font-bold">
                      <span className="text-blue-600">Version {h.version}</span>
                      <span className="text-slate-400">{h.updated_at?.split('T')[0]}</span>
                    </div>
                    <div className="text-slate-600">Updated by: <b>{h.updated_by}</b></div>
                  </div>
                ))
              )}
            </div>

            <div className="pt-2 border-t border-slate-100 flex justify-end">
              <button onClick={() => setHistoryPolicy(null)} className="px-4 py-2 bg-slate-900 text-white text-xs font-bold rounded-xl">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SOFT DELETE CONFIRM MODAL */}
      {deletePolicy && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-bold text-rose-600 text-base">Confirm Delete</h3>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete policy <b>{deletePolicy.policy_name}</b>?
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeletePolicy(null)} className="px-4 py-2 bg-slate-100 text-xs font-bold rounded-xl">
                Cancel
              </button>
              <button onClick={() => handleDeletePolicy(deletePolicy._id)} className="px-5 py-2 bg-rose-600 text-white text-xs font-bold rounded-xl">
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelPolicyMaster;
